<script lang="ts" setup>
import {computed, ComputedRef, ref} from 'vue'
import {CheckIcon} from '@heroicons/vue/20/solid'
import VSubscriptionPay from "@/scripts/components/app/billing/VSubscriptionPay.vue";
import {Plan, PlanType} from "@/scripts/types/account";

export interface Props {
    currentPlan: Plan,
    preSelectedPlanId: PlanType | null
}

const props = withDefaults(defineProps<Props>(), {
    preSelectedPlanId: null,
    currentPlan: null
});

const pricing = {
    frequencies: [
        {value: 'monthly', label: 'Ежемесячно', priceSuffix: '/месяц'},
        {value: 'annually', label: 'Ежегодно', priceSuffix: '/год'},
    ],
    tiers: [
        {
            name: 'Pro',
            id: 'pro',
            href: '#',
            price: {monthly: '290', annually: '5900'},
            features: [
                '50 QR кодов',
                '100,000 сканирований в месяц',
                'Расширенная аналитика',
                'Круглые QR коды',
            ],
            mostPopular: true,
            label: props.currentPlan.label == 'pro' ? 'продлить' : '',
            isAvailable: props.currentPlan.label != 'business'
        },
        {
            name: 'Business',
            id: 'business',
            href: '#',
            price: {monthly: '2790', annually: '27900'},
            features: [
                '200 QR кодов',
                'Безлимитное количество сканирований',
                'Расширенная аналитика',
                'Круглые QR коды',
                'Ретаргетинг',
                'Массовая генерация',
            ],
            mostPopular: false,
            label: props.currentPlan.label == 'pro' ? 'улучшить' : '',
            isAvailable: true
        },
    ],
}

const frequency = ref(pricing.frequencies[0])

const selectPlan = (plan: any) => {
    selectedPlan.value = plan
}

const availablePlans: ComputedRef<any[]> = computed(() => {
    return pricing.tiers.filter(item => item.isAvailable)
})

const selectedPlan = ref(availablePlans.value[0])
if (props.preSelectedPlanId !== "null") {
    selectedPlan.value = availablePlans.value.find(item => item.id === props.preSelectedPlanId)
}

</script>

<template>
    <main>
        <!-- Pricing section -->
        <div class="mx-auto max-w-7xl">
            <div
                class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-3">
                <div v-for="tier in availablePlans" :key="tier.id" @click.prevent="selectPlan(tier)"
                     :class="[tier.id === selectedPlan.id ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200', 'rounded-3xl p-8  cursor-pointer']">
                    <h2 :id="tier.id"
                        :class="[tier.id === selectedPlan.id ? 'text-indigo-600' : 'text-gray-900', 'text-lg font-semibold leading-8']">
                        {{ tier.name }}</h2>
                    <span v-if="tier.label.length > 0" class="inline-flex items-center rounded-md bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                        {{ tier.label }}
                    </span>

                    <p class="mt-6 flex items-baseline gap-x-1">
                        <span class="text-4xl font-bold tracking-tight text-gray-900">{{
                                tier.price[frequency.value]
                            }} ₽</span>
                        <span class="text-sm font-semibold leading-6 text-gray-600">{{ frequency.priceSuffix }}</span>
                    </p>
                    <ul role="list" class="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                        <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                            <CheckIcon class="h-6 w-5 flex-none text-indigo-600" aria-hidden="true"/>
                            {{ feature }}
                        </li>
                    </ul>
                </div>
            </div>
            <div class="ring-1 ring-gray-200 rounded-xl w-full sm:w-6/12 text-xs p-2.5 mt-4">
                Нужно больше? Безлимитные возможности и расширенные функции для большого бизнеса. Пишите: support@smartdots.ru
            </div>
        </div>

        <div class="mt-4 w-full sm:w-5/12">
            <v-subscription-pay :new-plan="selectedPlan.id" :current-plan="currentPlan"></v-subscription-pay>
        </div>
    </main>
</template>